import React, { useRef, useState } from 'react';
import { AlertTriangle, Bike, Car, Check, DollarSign, MapPin } from 'lucide-react';
import { VEHICLE_TYPE_LABELS, VEHICLE_TYPES } from '../../../../constants';
import './VehiclePickerContent.css';

const VEHICLE_TYPE_ICONS = {
    [VEHICLE_TYPES.MOTO]: Bike,
    [VEHICLE_TYPES.CARRO]: Car
};

export const getVehicleKey = (vehicle) =>
    vehicle.id ? `id-${vehicle.id}` : `plate-${vehicle.plate || 'sin-placa'}`;

const VehiclePickerContent = ({
    vehicles = [],
    onSelect,
    onOpenProfile,
    error = null,
    pickupAddress = 'Ubicación GPS',
    netTotal = 0
}) => {
    const [selectedKey, setSelectedKey] = useState(null);
    const optionRefs = useRef([]);

    const handleSelect = (vehicle) => {
        setSelectedKey(getVehicleKey(vehicle));
        if (onSelect) onSelect(vehicle);
    };

    const handleKeyDown = (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        if (vehicles.length === 0) return;
        event.preventDefault();

        const currentIndex = vehicles.findIndex(v => getVehicleKey(v) === selectedKey);
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const nextIndex = (currentIndex + delta + vehicles.length) % vehicles.length;
        const nextVehicle = vehicles[nextIndex];
        if (!nextVehicle) return;

        handleSelect(nextVehicle);
        if (optionRefs.current[nextIndex]) optionRefs.current[nextIndex].focus();
    };

    if (!vehicles || vehicles.length === 0) {
        return (
            <div className="bry-vehicle-picker">
                <div className="bry-vehicle-picker__empty">
                    <Car size={34} aria-hidden="true" />
                    <p className="bry-vehicle-picker__empty-title">No tienes vehículos registrados</p>
                    <p className="bry-vehicle-picker__empty-text">
                        Registra tu moto o carro en Mi Perfil para poder atender servicios.
                    </p>
                    <button
                        type="button"
                        className="bry-vehicle-picker__empty-cta"
                        onClick={() => onOpenProfile && onOpenProfile()}
                    >
                        Ir a Mi Perfil
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bry-vehicle-picker">
            {error && (
                <div className="bry-vehicle-picker__error" role="alert">
                    <AlertTriangle size={16} aria-hidden="true" />
                    <span>{error}</span>
                </div>
            )}

            <div className="bry-vehicle-picker__summary">
                <div className="bry-vehicle-picker__summary-row">
                    <MapPin size={14} aria-hidden="true" />
                    <span className="bry-vehicle-picker__summary-label">Recogida</span>
                    <span className="bry-vehicle-picker__summary-value" title={pickupAddress}>{pickupAddress}</span>
                </div>
                <div className="bry-vehicle-picker__summary-row">
                    <DollarSign size={14} aria-hidden="true" />
                    <span className="bry-vehicle-picker__summary-label">Neto a cobrar</span>
                    <span className="bry-vehicle-picker__summary-value bry-vehicle-picker__summary-value--net">
                        ${Number(netTotal || 0).toFixed(2)}
                    </span>
                </div>
            </div>

            <div
                role="radiogroup"
                aria-label="Vehículos registrados"
                className="bry-vehicle-picker__list"
                onKeyDown={handleKeyDown}
            >
                {vehicles.map((vehicle, index) => {
                    const key = getVehicleKey(vehicle);
                    const isSelected = key === selectedKey;
                    const Icon = VEHICLE_TYPE_ICONS[vehicle.vehicle_type] || Bike;
                    const metaParts = [vehicle.brand, vehicle.model, vehicle.year, vehicle.color]
                        .filter(part => part !== null && part !== undefined && part !== '');

                    return (
                        <button
                            key={key}
                            ref={element => { optionRefs.current[index] = element; }}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            tabIndex={isSelected || (selectedKey === null && index === 0) ? 0 : -1}
                            className={`bry-vehicle-option${isSelected ? ' is-selected' : ''}`}
                            onClick={() => handleSelect(vehicle)}
                        >
                            <span className="bry-vehicle-option__radio" aria-hidden="true">
                                {isSelected && <Check size={14} strokeWidth={3} />}
                            </span>
                            <span className="bry-vehicle-option__icon" aria-hidden="true">
                                <Icon size={20} />
                            </span>
                            <span className="bry-vehicle-option__body">
                                <span className="bry-vehicle-option__plate">{vehicle.plate || 'SIN-PLACA'}</span>
                                <span className="bry-vehicle-option__meta">
                                    {VEHICLE_TYPE_LABELS[vehicle.vehicle_type] || vehicle.vehicle_type}
                                    {metaParts.length > 0 ? ` · ${metaParts.join(' · ')}` : ''}
                                </span>
                            </span>
                            {vehicle.is_default && (
                                <span className="bry-vehicle-option__badge">Principal</span>
                            )}
                            {!vehicle.id && (
                                <span className="bry-vehicle-option__badge bry-vehicle-option__badge--pending">Sin registrar</span>
                            )}
                            {vehicle.is_verified && (
                                <span className="bry-vehicle-option__badge bry-vehicle-option__badge--verified">Verificado</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <p className="bry-vehicle-picker__hint">
                Cancelar no rechaza el servicio. Seguirás viéndolo en tus solicitudes.
            </p>
        </div>
    );
};

export default VehiclePickerContent;
